/**
 * Data access for the product entity (catalog list + detail). Read-only.
 *
 * All filtering, searching and sorting are **server-side** (the frontend is
 * untrusted): query params are forwarded verbatim to ``GET /api/catalog/products/``.
 */
import { useApiQuery, type QueryState } from "@shared/api";

import type { ProductDetail, ProductListItem, ProductSort } from "./model";

export const PRODUCTS_PATH = "/catalog/products/";

export interface ProductListParams {
  readonly game?: number;
  readonly category?: number;
  readonly search?: string;
  readonly sort?: ProductSort;
}

function buildProductsPath(params: ProductListParams): string {
  const search = new URLSearchParams();
  if (params.game !== undefined) {
    search.set("game", String(params.game));
  }
  if (params.category !== undefined) {
    search.set("category", String(params.category));
  }
  if (params.search !== undefined && params.search.trim().length > 0) {
    search.set("search", params.search.trim());
  }
  if (params.sort !== undefined) {
    search.set("sort", params.sort);
  }
  const qs = search.toString();
  return qs.length > 0 ? `${PRODUCTS_PATH}?${qs}` : PRODUCTS_PATH;
}

/** Fetch the server-filtered product list. Re-fetches when any param changes. */
export function useProducts(
  params: ProductListParams,
): QueryState<readonly ProductListItem[]> {
  return useApiQuery<readonly ProductListItem[]>(buildProductsPath(params), {
    deps: [params.game, params.category, params.search, params.sort],
  });
}

/** Fetch a single product's full detail (with active variants). */
export function useProduct(
  productId: number | undefined,
): QueryState<ProductDetail> {
  const enabled = productId !== undefined;
  const path =
    productId !== undefined
      ? `${PRODUCTS_PATH}${productId}/`
      : PRODUCTS_PATH;
  return useApiQuery<ProductDetail>(path, { enabled, deps: [productId] });
}
